/**
 * FloatingButton Component
 * 
 * Main floating button that toggles the chat widget open/closed.
 * Shows different icons based on state (collapsed, open, expanded).
 * 
 * Design: Minimal, professional with smooth animations.
 */

import { MessageCircle, X, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import chatbotIcon from '/src/assets/historyBlueBotIcon.svg';

interface FloatingButtonProps {
  isOpen: boolean;
  isExpanded: boolean;
  isSmallScreen: boolean;
  onClick: () => void;
}

export function FloatingButton({
  isOpen,
  isExpanded,
  isSmallScreen,
  onClick,
}: FloatingButtonProps) {
  const shouldHideOnMobile = isSmallScreen && isOpen;

  const getButtonContent = () => {
    if (isExpanded) {
      // Rotated X (plus rotated 45deg) for minimal close effect
      return (
        <Plus 
          className={cn(
            "h-7 w-7 lg:h-8 lg:w-8 transition-all duration-300",
            "rotate-45"
          )} 
        />
      );
    } else if (isOpen) {
      // Chat is open, show chatbot icon (smaller size)
      return (
        <img 
          src={chatbotIcon} 
          alt="Chatbot" 
          className="h-6 w-6 lg:h-7 lg:w-7 transition-all duration-300"
        />
      );
    } else {
      // Default state - message circle (larger when collapsed)
      return (
        <MessageCircle 
          className="h-7 w-7 lg:h-8 lg:w-8 transition-all duration-300" 
        />
      );
    }
  };

  if (shouldHideOnMobile) {
    return null;
  }

  return (
    <Button
      onClick={onClick}
      size="icon"
      style={{ zIndex: 9999 }}
      className={cn(
        'fixed bottom-6 right-6',
        
        // Size - larger when collapsed, smaller when open to avoid overlap
        isOpen 
          ? 'h-14 w-14 lg:h-16 lg:w-16'
          : 'h-16 w-16 lg:h-20 lg:w-20',
        'rounded-full',
        
        'shadow-lg',
        
        'transition-all duration-300 ease-out',
        'hover:shadow-xl active:shadow-md',
        
        'hover:scale-[1.05] active:scale-95',
        
        'select-none',
        
        'relative',
        
        // State-based styles - minimal and clean
        isExpanded 
          ? 'bg-gray-800 hover:bg-gray-700 text-white' 
          : isOpen 
            ? 'bg-primary hover:bg-primary/90 text-primary-foreground' 
            : 'bg-gray-900 hover:bg-gray-800 text-white',
        
        // Pulse ring effect when collapsed - subtle attention grabber from center
        !isOpen && !isExpanded && [
          'before:absolute before:inset-0 before:rounded-full',
          'before:border-2 before:border-white/25',
          'before:animate-[pulseRing_3.5s_ease-out_infinite]',
        ]
      )}
      aria-label={isOpen ? 'Cerrar chat' : isExpanded ? 'Cerrar opciones' : 'Abrir opciones'}
      draggable={false}
    >
      <div className="relative z-10 flex items-center justify-center">
        {getButtonContent()}
      </div>
    </Button>
  );
}

